import type { ContentActionPayload } from '~/features/analytix/ui/models/content-actions'
import type {
  RemoveFromContinueWatchingCommand,
  RemoveFromContinueWatchingRoute,
} from './index.h'

import { api } from '@setplex/tria-api'
import { createEvent, restore, sample } from 'effector'
import { contentAction } from '~/features/analytix/ui/models/content-actions'

type VodRemoveCommand = Extract<
  RemoveFromContinueWatchingCommand,
  { type: 'vod' }
>
type EpisodeWithSeasonRemoveCommand = Extract<
  RemoveFromContinueWatchingCommand,
  { type: 'episodeWithSeason' }
>
type EpisodeWithoutSeasonRemoveCommand = Extract<
  RemoveFromContinueWatchingCommand,
  { type: 'episodeWithoutSeason' }
>

export const removeFromContinueWatching =
  createEvent<RemoveFromContinueWatchingCommand>()

export const removeFromContinueWatchingFailed =
  createEvent<RemoveFromContinueWatchingRoute>()

export const $removeFromContinueWatchingFailed = restore(
  removeFromContinueWatchingFailed,
  null
)

sample({
  clock: removeFromContinueWatching,
  fn: (command): ContentActionPayload => ({
    contentId: command.type === 'vod' ? command.vodId : command.tvShowId,
    contentType:
      command.analyticsContentType ??
      (command.type === 'vod' ? 'vod' : 'tvshow'),
    contentName: command.analyticsContentName ?? command.name,
    programName: command.analyticsProgramName,
    routeName: command.analyticsRouteName,
    uiElementType: 'menu_option',
    uiElementName: 'remove_from_cw',
    actionName: 'tap',
  }),
  target: contentAction,
})

sample({
  clock: removeFromContinueWatching,
  filter: (command) => command.type === 'vod',
  fn: (command) => {
    const { vodId } = command as VodRemoveCommand
    return { vodId }
  },
  target: api.vods.removeContinueWatchingFx,
})

sample({
  clock: removeFromContinueWatching,
  filter: (command) => command.type === 'episodeWithSeason',
  fn: (command) => {
    const { tvShowId, seasonId, episodeId } =
      command as EpisodeWithSeasonRemoveCommand

    return { tvShowId, seasonId, episodeId }
  },
  target: api.tvshows.removeContinueWatchingTvShowFx,
})

sample({
  clock: removeFromContinueWatching,
  filter: (command) => command.type === 'episodeWithoutSeason',
  fn: (command) => {
    const { tvShowId, episodeId } = command as EpisodeWithoutSeasonRemoveCommand

    return { tvShowId, episodeId }
  },
  target: api.tvshows.removeContinueWatchingWithoutSeasonFx,
})

sample({
  clock: api.vods.removeContinueWatchingFx.fail,
  fn: ({ params }): RemoveFromContinueWatchingRoute => ({
    type: 'vod',
    vodId: params.vodId,
  }),
  target: removeFromContinueWatchingFailed,
})

sample({
  clock: api.tvshows.removeContinueWatchingTvShowFx.fail,
  fn: ({ params }): RemoveFromContinueWatchingRoute => ({
    type: 'episodeWithSeason',
    tvShowId: params.tvShowId,
    seasonId: params.seasonId,
    episodeId: params.episodeId,
  }),
  target: removeFromContinueWatchingFailed,
})

sample({
  clock: api.tvshows.removeContinueWatchingWithoutSeasonFx.fail,
  fn: ({ params }): RemoveFromContinueWatchingRoute => ({
    type: 'episodeWithoutSeason',
    tvShowId: params.tvShowId,
    episodeId: params.episodeId,
  }),
  target: removeFromContinueWatchingFailed,
})
